import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModalContainer from '@components/modals/ModalContainer';
import CommonImage from '@components/CommonImage';
import ModalHeader from '@components/modals/ModalHeader';

import AddReview from '@features/supplier/component/AddReview';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getExFactorySupplierQuote, updateExFactorySupplierQuote } from '@services/quoteServices';

import { renderMultiColor, validateFloatNumber, validateNumber } from '@utils/Util';

const ExFactorySupplierQuote = ({ onCloseModal, rfqId, onSuccess, supplierDetails }) => {
	const [isShowReviewModal, setIsShowReviewModal] = useState(false);
	const [quotes, setQuotes] = useState({});
	const [errors, setErrors] = useState({});
	const [buyerPriceUpdated, setBuyerPriceUpdated] = useState(false);
	const [factoryPriceUpdated, setFactoryPriceUpdated] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		(async () => {
			const data = await getExFactorySupplierQuote(rfqId);
			setQuotes(data);
		})();
	}, [rfqId]);

	const renderSizeWiseTableHeading = () => {
		return (
			<thead>
				<tr>
					{quotes?.colorWiseSizeQuantityPairList?.length > 0 &&
						quotes.colorWiseSizeQuantityPairList[0].sizeQuantityPairList.map((value) => (
							<th>
								<p className="semibold-14 col-heading" key={value.id}>
									{value.label}
								</p>
							</th>
						))}
				</tr>
			</thead>
		);
	};

	const renderQuoteTypes = (type) => {
		const labelType = type === 'BUYER' ? 'Buyer' : 'Factory';
		const quotationType = type === 'BUYER' ? quotes?.buyerQuotationType : quotes?.factoryQuotationType;
		const priceType = quotes?.factoryPriceType;
		return (
			<div className="d-flex justify-content-between align-self-center my-3">
				<p className="regular-14 gray_dark_02">{labelType} price</p>
				<div className="d-flex justify-content-between align-self-center gap-3">
					{quotationType && (
						<p className="gray_dark_02 regular-14">
							{quotationType} <CommonImage src="/icons/down-arrow.png" />
						</p>
					)}
					{priceType && (
						<p className="gray_dark_02 regular-14 ml-2">
							{priceType} <CommonImage src="/icons/down-arrow.png" />
						</p>
					)}
				</div>
			</div>
		);
	};

	const getSizeWisePrice = (sizeWisePrices, id) => {
		if (sizeWisePrices[id]) {
			return sizeWisePrices[id];
		}
		return null;
	};

	const getColorWisePrice = (colorWisePrices, id) => {
		if (colorWisePrices[id]) {
			return colorWisePrices[id];
		}
		return null;
	};

	const renderFactoryWiseTableHeading = () => {
		if (quotes?.factoryQuotationType === 'COLORWISE') {
			return (
				<thead>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList &&
							quotes.colorWiseSizeQuantityPairList.map((value) => (
								<th key={value.id}>
									<span>{renderMultiColor(value)}</span>
								</th>
							))}
					</tr>
				</thead>
			);
		} else if (quotes?.factoryQuotationType === 'SIZEWISE') {
			return renderSizeWiseTableHeading();
		}
	};

	const onChangePriceSizeOrColorWise = (quoteType, priceType, e, sizeId) => {
		const { value } = e.target;
		let fieldName = '';
		if (quoteType === 'BUYER' && priceType === 'COLORWISE') {
			fieldName = 'colorWiseBuyerPrice';
		}
		if (quoteType === 'BUYER' && priceType === 'SIZEWISE') {
			fieldName = 'sizeWiseBuyerPrice';
		}
		if (quoteType !== 'BUYER' && priceType === 'COLORWISE') {
			fieldName = 'colorWiseFactoryPrice';
		}
		if (quoteType !== 'BUYER' && priceType === 'SIZEWISE') {
			fieldName = 'sizeWiseFactoryPrice';
		}
		let modifiedPrice = { ...quotes[fieldName] };
		modifiedPrice[sizeId] = value;
		setQuotes((prev) => ({ ...prev, [fieldName]: modifiedPrice }));
		if (quoteType === 'BUYER') {
			setBuyerPriceUpdated(true);
		} else {
			setFactoryPriceUpdated(true);
		}
	};

	const renderTableBody = (type) => {
		let quotationType = type === 'BUYER' ? quotes?.buyerQuotationType : quotes?.factoryQuotationType;
		let colorWisePrice = type === 'BUYER' ? quotes?.colorWiseBuyerPrice : quotes?.colorWiseFactoryPrice;
		let sizeWisePrice = type === 'BUYER' ? quotes?.sizeWiseBuyerPrice : quotes?.sizeWiseFactoryPrice;
		if (quotationType === 'COLORWISE') {
			return (
				<tbody>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList &&
							quotes.colorWiseSizeQuantityPairList.map((value) => (
								<td key={value.id}>
									<input
										type="number"
										min={1}
										value={getColorWisePrice(colorWisePrice, value.id)}
										placeholder="$00.00"
										onChange={(e) =>
											onChangePriceSizeOrColorWise(type, quotationType, e, value.id)
										}
										onKeyPress={validateFloatNumber}
									/>
								</td>
							))}
					</tr>
				</tbody>
			);
		} else if (quotationType === 'SIZEWISE') {
			return (
				<tbody>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList.length > 0 &&
							quotes.colorWiseSizeQuantityPairList[0].sizeQuantityPairList.map((value) => (
								<td key={value.id}>
									<input
										type="number"
										min={1}
										placeholder="$00.00"
										value={getSizeWisePrice(sizeWisePrice, value.id)}
										onChange={(e) =>
											onChangePriceSizeOrColorWise(type, quotationType, e, value.id)
										}
										onKeyPress={validateFloatNumber}
									/>
								</td>
							))}
					</tr>
				</tbody>
			);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setQuotes((prev) => ({ ...prev, [name]: value }));
		if (name === 'designWiseBuyerPrice') {
			setBuyerPriceUpdated(true);
		}
		if (name === 'designWiseFactoryPrice') {
			setFactoryPriceUpdated(true);
		}
	};

	const isInvalid = () => {
		let errorObj = {};
		if (!quotes?.exFactoryDate) {
			errorObj['exFactoryDate'] = 'Delivery date is required!';
		}
		if (!quotes?.exFactoryQuantity) {
			errorObj['exFactoryQuantity'] = 'Quantity is required!';
		}
		if (quotes?.buyerQuotationType === 'DESIGNWISE' && !quotes?.designWiseBuyerPrice) {
			errorObj['designWiseBuyerPrice'] = 'Price is required!';
		}
		if (quotes?.factoryQuotationType === 'DESIGNWISE' && !quotes?.designWiseFactoryPrice) {
			errorObj['designWiseFactoryPrice'] = 'Price is required!';
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length > 0;
	};

	const handleSubmit = async () => {
		if (!isInvalid()) {
			try {
				dispatch(openLoader());
				await updateExFactorySupplierQuote(rfqId, {
					...quotes,
					buyerPriceUpdated,
					factoryPriceUpdated
				});
				if (quotes?.needFactoryFeedback === true) {
					setIsShowReviewModal(true);
				} else {
					onSuccess();
				}
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	return (
		<>
			{!isShowReviewModal && (
				<ModalContainer onCloseModal={onCloseModal} className="color-and-size-order-modal">
					<div className="popup-wrapper width-720-modal">
						<div className="common-popup color-and-size-order-modal">
							<ModalHeader title="Shipment Details" onClose={onCloseModal} />
							<div className="common-popup-body scroll-y-label">
								<div className="delivery-and-qunt-header">
									<div className="container px-0">
										<div className="row">
											<div className="col-md-6">
												<div className="from-group">
													<label>Ex-factory date</label>
													<input
														type="date"
														name="exFactoryDate"
														value={quotes?.exFactoryDate || ''}
														onChange={handleChange}
													/>
													{errors?.exFactoryDate && (
														<p className="error d-block">
															{errors.exFactoryDate}
														</p>
													)}
												</div>
											</div>
											<div className="col-md-6">
												<div className="from-group">
													<label>Total shipped quantity</label>
													<input
														type="text"
														name="exFactoryQuantity"
														value={quotes?.exFactoryQuantity || ''}
														onChange={handleChange}
														onKeyPress={validateNumber}
													/>
													{errors?.exFactoryQuantity && (
														<p className="error d-block">
															{errors.exFactoryQuantity}
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="mt-4">
									<div className="create-order-container p-0">
										<div className="design-quantity-table">
											{renderQuoteTypes('BUYER')}
											<div className="design-table d-block">
												<div className="category-wise-table sizewise-wise-table">
													{quotes?.buyerQuotationType !== 'DESIGNWISE' && (
														<table className="table">
															{renderSizeWiseTableHeading()}
															{renderTableBody('BUYER')}
														</table>
													)}
													{quotes?.buyerQuotationType === 'DESIGNWISE' && (
														<div className="d-flex justify-content-end">
															<div className="from-group">
																<input
																	type="text"
																	value={quotes?.designWiseBuyerPrice || ''}
																	name="designWiseBuyerPrice"
																	onChange={handleChange}
																	onKeyPress={validateFloatNumber}
																/>
																{errors?.designWiseBuyerPrice && (
																	<p className="error">
																		{errors.designWiseBuyerPrice}
																	</p>
																)}
															</div>
														</div>
													)}
												</div>
											</div>
										</div>

										<div className="design-quantity-table">
											{renderQuoteTypes('FACTORY')}
											<div className="design-table d-block">
												<div className="category-wise-table sizewise-wise-table">
													{quotes?.factoryQuotationType !== 'DESIGNWISE' && (
														<table className="table">
															{renderFactoryWiseTableHeading()}
															{renderTableBody('FACTORY')}
														</table>
													)}
													{quotes?.factoryQuotationType === 'DESIGNWISE' && (
														<div className="d-flex justify-content-end align-self-center">
															<div className="from-group">
																<input
																	type="text"
																	value={
																		quotes?.designWiseFactoryPrice || ''
																	}
																	name="designWiseFactoryPrice"
																	onChange={handleChange}
																	onKeyPress={validateFloatNumber}
																/>
																{errors?.designWiseFactoryPrice && (
																	<p className="error">
																		{errors.designWiseFactoryPrice}
																	</p>
																)}
															</div>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="modal-footer">
								<div className="footer-buttons text-right">
									<button className="button outline size32 ml-2" onClick={onCloseModal}>
										Cancel
									</button>
									<button className="button size32 ml-2" onClick={handleSubmit}>
										Submit
									</button>
								</div>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{isShowReviewModal && (
				<AddReview
					onSuccess={onSuccess}
					onCloseReview={onCloseModal}
					supplierDetails={supplierDetails}
				/>
			)}
		</>
	);
};

export default ExFactorySupplierQuote;
